import { IntlShape } from 'react-intl';
import { ITransaction } from '../api/transactions/types';
import { EStationPricingOption } from '../api/types';

export const toAbsoluteUrl = (link?: string | null): string => {
    const trimmed = link?.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed.replace(/^\/+/, '')}`;
};

export const dateWithoutTimeZone = (date: number | string | Date) => {
    const currentDate = new Date(date);
    return new Date(currentDate.toISOString().slice(0, 19).replace('T', ' '));
};

export function is24HourFormat() {
    // Get a formatted time string using the locale's time format
    const formattedTime = new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: 'numeric',
    }).format(new Date());

    // Check if the formatted time includes AM or PM
    return (
        !formattedTime.toLowerCase().includes('am') && !formattedTime.toLowerCase().includes('pm')
    );
}

export const getDurationInMinutes = (
    start: number | string | Date,
    end?: number | string | Date
): number => {
    const startNew = dateWithoutTimeZone(start);
    const endNew = end ? dateWithoutTimeZone(end) : dateWithoutTimeZone(new Date());
    return Math.floor(Math.abs(endNew.getTime() - startNew.getTime()) / (1000 * 60));
};

export const getTariff = (
    guest: ITransaction,
    formatMessage: ({ id }: { id: string }) => string
): string[] => {
    const pricing: any = guest?.pricing ?? {};
    const currency = pricing.currency ?? guest?.currency ?? '';
    const pricingOption: 'free' | 'kwh' | 'hour' = pricing.pricingOption ?? EStationPricingOption.kwh;
    const type: 'fixed' | 'tiered' = pricing.type ?? 'fixed';

    const perLabel = formatMessage({ id: 'connector.info.per' }).toLowerCase();
    const unit = pricingOption === EStationPricingOption.hour ? formatMessage({ id: 'unit.h' }) : formatMessage({ id: 'unit.kWh' });

    if (pricingOption === EStationPricingOption.free) {
        return [formatMessage({ id: 'payment.free' }), ''];
    }

    const basePriceMinor =
        pricingOption === EStationPricingOption.hour
            ? (pricing.priceForHour ?? 0)
            : (pricing.priceForKwh ?? 0);

    const tieredPeriods = Array.isArray(pricing.tieredPricesPeriods) ? pricing.tieredPricesPeriods : [];
    if (type === 'tiered' && tieredPeriods.length > 0) {
        // `quantity` is a START threshold. Before the first start point, use base price.
        const sorted = [...tieredPeriods].sort((a, b) => (a.quantity ?? 0) - (b.quantity ?? 0));
        const metric =
            pricing.tieredPricingOption === 'hour'
                ? Math.max(0, (Date.now() - new Date(guest?.transaction?.startAt).getTime()) / 1000 / 60) / 60
                : Math.max(0, (guest?.transaction?.energy ?? 0) / 1000);

        const firstStartQ = sorted?.[0]?.quantity ?? 0;
        const priceMinor =
            metric < firstStartQ
                ? basePriceMinor
                : // pick last tier whose start <= metric
                  (sorted.reduce<number>((acc, t) => {
                      const q = t.quantity ?? 0;
                      if (metric >= q) return t.price ?? acc;
                      return acc;
                  }, basePriceMinor) ?? basePriceMinor);
        return [
            `${Number(priceMinor / 100).toFixed(2)}`,
            `${currency} ${perLabel} ${unit}`,
        ];
    }

    // Fixed: if we have periods for today, choose segment price; otherwise base.
    const periods = Array.isArray(pricing.pricesPeriods) ? pricing.pricesPeriods : [];
    if (periods.length > 0) {
        const now = new Date();
        const dayMon0 = (now.getDay() + 6) % 7;
        const nowMin = now.getHours() * 60 + now.getMinutes();

        const dayPeriods = periods
            .filter((p: any) => Array.isArray(p.days) && p.days.includes(dayMon0))
            .map((p: any) => ({
                startMin: Math.min(24 * 60, Math.max(0, (p.startHours ?? 0) * 60 + (p.startMinutes ?? 0))),
                endMin: Math.min(24 * 60, Math.max(0, (p.endHours ?? 0) * 60 + (p.endMinutes ?? 0))),
                price: p.price ?? 0,
            }))
            .filter((p: any) => p.endMin > p.startMin)
            .sort((a: any, b: any) => a.startMin - b.startMin);

        // Find matching period; otherwise base (off-period)
        const current = dayPeriods.find((p: any) => nowMin >= p.startMin && nowMin < p.endMin);
        const priceMinor = current?.price ?? basePriceMinor;

        return [
            `${Number(priceMinor / 100).toFixed(2)}`,
            `${currency} ${perLabel} ${unit}`,
        ];
    }

    return [
        `${Number(basePriceMinor / 100).toFixed(2)}`,
        `${currency} ${perLabel} ${unit}`,
    ];
};

export function formatDateToLocaleString(date: Date, locale: string = 'en-US'): string {
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };

    const localeMap: Record<string, string> = {
        en: 'en-US',
        es: 'es-ES',
        fr: 'fr-FR',
        ua: 'uk-UA',
    };

    const mappedLocale = localeMap[locale] || locale;
    return date.toLocaleDateString(mappedLocale, options);
}

export function formatMinutes(
    totalMinutes: number,
    formatMessage: ({ id }: { id: string }) => string
) {
    const hours = Math.floor(totalMinutes / 60); // Calculate hours
    const minutes = totalMinutes % 60; // Calculate remaining minutes

    return {
        hours: `${hours} ${formatMessage({ id: 'unit.h' })}`,
        minutes: `${minutes} ${formatMessage({ id: 'unit.minute' })}`,
    };
}

export function formatMinutesToObject(totalMinutes: number) {
    const hours = Math.floor(totalMinutes / 60); // Calculate hours
    const minutes = totalMinutes % 60; // Calculate remaining minutes

    return {
        hours: hours < 10 ? `0${hours}` : hours,
        minutes: minutes < 10 ? `0${minutes}` : minutes,
    };
}

export const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

export const getTime = (targetTime: Date | string) => {
    const __ = (_: number) => (`${_}`.length < 2 ? `0${_}` : `${_}`);
    const time = new Date(targetTime);
    return `${__(time.getHours())}:${__(time.getMinutes())}`;
};

export const getEstimationCompletition = (guest: ITransaction) =>
    guest.estimation ? getTime(guest.estimation.completionAt) : undefined;

export function getLetterByIndex(index: number, uppercase = true): string | null {
    if (index < 0 || index > 25) return null; // Ensure index is within range

    const baseCharCode = uppercase ? 65 : 97; // ASCII codes for 'A' and 'a'
    return String.fromCharCode(baseCharCode + index);
}

export const getDistanceFromMeters = (distance: number, isMiles: boolean, intl: IntlShape) => {
    const METERS_TO_MILES = 0.000621371;
    const METERS_TO_FEET = 3.28084;

    if (isMiles) {
        if (distance < 1609.34) {
            return `${(distance * METERS_TO_FEET).toFixed(0)} ${intl.formatMessage({ id: 'units.ft' })}`; // Return in feet
        }
        return `${(distance * METERS_TO_MILES).toFixed(2)} ${intl.formatMessage({ id: 'units.mi' })}`; // Return in miles
    } else {
        if (distance < 1000) {
            return `${distance.toFixed(0)} ${intl.formatMessage({ id: 'units.m' })}`; // Return in meters
        }
        return `${(distance / 1000).toFixed(2)} ${intl.formatMessage({ id: 'units.km' })}`; // Return in kilometers
    }
};
